import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { renameUploadedFiles } from '~/components/presets/presets.utils';

import { TemplateWorkspaceDialog } from './template-workspace-dialog';

const TEMPLATE_INSTRUCTIONS = `**Create a reusable template with dynamic sections.**

I will save the output to my template library, so produce a generic, reusable template — not a filled-out document. Replace any sample-specific content (names, dates, dollar amounts, addresses, policy numbers, etc.) with clearly named placeholders such as [[CLIENT_NAME]], [[EFFECTIVE_DATE]], [[POLICY_NUMBER]], [[AGENT_NAME]]. Be very careful on coverage information, and quote or policy information, this info will typically vary from client to client, so make sure to put an appropriately detailed placeholder for this information. Anytime the template lists coverage, exclusion, or other information this will vary with placeholders unless specifically told otherwise.
The Agency Name (ie the company name) should be fixed to whoever is creating the template based on their account details, but the agent name should be a placeholder that can be replaced with the actual agent name.

Produce one complete PDF document suitable for the template library and PDF export.`;

export type CreateTemplateWorkspaceDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateTemplateWorkspaceDialog = (props: CreateTemplateWorkspaceDialogProps) => {
  const v4On = useFlag(FEATURE_FLAGS.DOCUMENT_GENERATION_V4);
  const brandOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const designsOn = v4On && brandOn;

  return (
    <TemplateWorkspaceDialog
      {...props}
      title="Create template"
      description="Start a workspace where Cara drafts a reusable template you can save to your library. Add optional notes or reference files below."
      instructionsLabel="Additional instructions"
      instructionsDescription="Describe layout, sections, tone, or anything Cara should follow when drafting the template."
      instructionsRows={5}
      filesDescription="Upload PDFs or other files Cara should use as context when designing the template."
      guidance={
        <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 dark:border-sky-800 dark:bg-sky-950/40">
          {designsOn ? (
            <>
              <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                Template sections now, Design later
              </div>
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                This flow creates a template with dynamic content sections. Colors, fonts, headers
                and footers come from the design, in Settings → Design. Start and end pages are
                chosen on the template once it is created.
              </p>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                Template sections now, Style later
              </div>
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                This flow creates a template with dynamic content sections. Font, color, headers and
                footers, and static pages to include can be edited after the template is created, in
                the linked Style.
              </p>
            </>
          )}
        </div>
      }
      buildRequest={(message, files) => ({
        message: message
          ? `${TEMPLATE_INSTRUCTIONS}\n\n**Additional instructions from the user:**\n\n${message}`
          : TEMPLATE_INSTRUCTIONS,
        files: renameUploadedFiles(files, '[Reference] ')
      })}
    />
  );
};
